import {
    AssistantMessageStatusReason,
    AssistantMessageStatusType,
    ChatRole,
    ChatSystemType,
} from 'utils/chat/chatConstants';

const getMessageSystemType = (message) => message?.system_type ?? message?.metadata?.custom?.system_type;
const getMessageSystemData = (message) => message?.system_data ?? message?.metadata?.custom?.system_data;

export const buildAssignmentChatFilter = ({ assignmentId, courseId, ownerAccountId }) => ({
    active: true,
    owner_account_id: ownerAccountId,
    course_id: courseId,
    ...(assignmentId ? { assignment_id: assignmentId } : {}),
});

export const sortSubmissionsByCreatedOn = (submissions = []) => {
    return [...submissions]
        .filter((submission) => submission?.created_on)
        .sort((left, right) => (left.created_on || 0) - (right.created_on || 0));
};

export const sortChatMessagesByCreatedOn = (messages = []) => {
    return [...messages].sort((left, right) => {
        const leftCreatedOn = left?.created_on || 0;
        const rightCreatedOn = right?.created_on || 0;

        if (leftCreatedOn !== rightCreatedOn) {
            return leftCreatedOn - rightCreatedOn;
        }

        return (left?.modified_on || leftCreatedOn) - (right?.modified_on || rightCreatedOn);
    });
};

export const buildSubmissionCreatedSystemMessage = (submission, index) => {
    return {
        id: `submission-created-${submission.id}`,
        active: true,
        role: ChatRole.SYSTEM,
        created_on: submission.created_on,
        modified_on: submission.created_on,
        message_text: `Draft ${index + 1} has been submitted at`,
        message_context: null,
        system_type: ChatSystemType.SUBMISSION_CREATED,
        system_data: {
            draftNumber: index + 1,
        },
    };
};

export const getSubmissionCreatedDraftNumber = (message) => {
    if (!message || message.role !== ChatRole.SYSTEM) return null;

    const systemType = getMessageSystemType(message);
    const systemData = getMessageSystemData(message);

    if (systemType === ChatSystemType.SUBMISSION_CREATED && typeof systemData?.draftNumber === 'number') {
        return systemData.draftNumber;
    }

    const text = `${message.message_text || getMessageText(message.content || []) || ''}`;
    const draftNumberMatch = text.match(/^Draft\s+(\d+)\s+has been submitted at$/i);
    if (!draftNumberMatch) return null;

    return Number(draftNumberMatch[1]);
};

export const hasSubmissionCreatedSystemMessage = ({ chat, draftNumber, messageId }) => {
    return (chat?.messages || []).some((message) => {
        if (message?.id === messageId) return true;
        return getSubmissionCreatedDraftNumber(message) === draftNumber;
    });
};

const getLatestMessageByRole = ({ chat, role, systemType }) => {
    const messages = sortChatMessagesByCreatedOn(chat?.messages || []);

    return (
        [...messages].reverse().find((message) => {
            if (message?.role !== role) return false;
            if (!systemType) return true;
            return getMessageSystemType(message) === systemType;
        }) || null
    );
};

export const getLatestChatMessage = (chat) => {
    const messages = sortChatMessagesByCreatedOn(chat?.messages || []);
    if (messages.length > 0) {
        return [...messages].reverse().find((message) => message?.role !== ChatRole.SYSTEM) || null;
    }

    if (chat?.latest_message) {
        return chat.latest_message;
    }

    return null;
};

const getLatestSubmissionCreatedMessage = (chat) => {
    return getLatestMessageByRole({
        chat,
        role: ChatRole.SYSTEM,
        systemType: ChatSystemType.SUBMISSION_CREATED,
    });
};

export const shouldShowChatSuggestions = ({ chat }) => {
    const latestChatMessage = getLatestMessageByRole({ chat, role: ChatRole.USER });
    const latestSubmissionCreatedMessage = getLatestSubmissionCreatedMessage(chat);

    if (!latestSubmissionCreatedMessage) {
        return !latestChatMessage;
    }

    if (!latestChatMessage) {
        return true;
    }

    return latestSubmissionCreatedMessage.created_on > latestChatMessage.created_on;
};

export const hasUnreadChat = ({ chat, accountId }) => {
    if (!chat || !accountId) return false;

    const latestMessage = getLatestChatMessage(chat);
    if (!latestMessage) return false;
    if (latestMessage.role === ChatRole.USER) return false;

    const readEntry = (chat.read_state || []).find((item) => item.account_id === accountId);
    return readEntry?.last_read_message_id !== latestMessage.id;
};

export const toAssistantMessages = (messages = []) => {
    return messages.map((message) => ({
        ...(message.role === ChatRole.ASSISTANT
            ? {
                  status: {
                      type: message.is_streaming
                          ? AssistantMessageStatusType.RUNNING
                          : AssistantMessageStatusType.COMPLETE,
                      ...(message.is_streaming ? {} : { reason: AssistantMessageStatusReason.STOP }),
                  },
              }
            : {}),
        ...(message.role === ChatRole.USER
            ? {
                  attachments: [],
              }
            : {}),
        id: message.id,
        role: message.role,
        createdAt: new Date(message.created_on || Date.now()),
        content: [{ type: 'text', text: message.message_text || '' }],
        metadata: {
            ...(message.role === ChatRole.ASSISTANT
                ? {
                      unstable_annotations: [],
                      unstable_data: [],
                      unstable_state: null,
                      steps: [],
                  }
                : {}),
            custom: {
                message_context: message.message_context,
                context: message.context,
                page_context: message.page_context,
                help_center_match_ids: message.help_center_match_ids,
                requested_interaction: message.requested_interaction,
                interaction_response: message.interaction_response,
                system_type: message.system_type,
                system_data: message.system_data,
            },
        },
    }));
};

export const getMessageText = (parts = []) => {
    return parts
        .filter((part) => part.type === 'text')
        .map((part) => part.text || '')
        .join('\n\n');
};

export const getChatSystemMessageText = ({ fallbackText = '', includeTimestamp = false, message, t, timeLabel }) => {
    const systemType = getMessageSystemType(message);
    const messageText = fallbackText || message?.message_text || getMessageText(message?.content || []);
    const submissionCreatedDraftNumber = getSubmissionCreatedDraftNumber(message);

    if (systemType === ChatSystemType.SUBMISSION_CREATED || submissionCreatedDraftNumber !== null) {
        return t('assignment:chat.submission-created-text-with-time', '{{draftLabel}} has been submitted at {{time}}', {
            draftLabel: t('assignment:draft-number', 'Draft {{count}}', {
                count: submissionCreatedDraftNumber,
            }),
            time: timeLabel,
        });
    }

    if (includeTimestamp && timeLabel) {
        return t('chat.system-message-with-time', {
            defaultValue: '{{message}} {{time}}',
            message: messageText,
            time: timeLabel,
        });
    }

    return messageText;
};

export const getChatExportMessageText = ({ message, t, timeLabel }) => {
    if (message.role !== ChatRole.SYSTEM) return message.message_text || '';

    return getChatSystemMessageText({
        fallbackText: message.message_text || '',
        message,
        t,
        timeLabel,
    });
};

const normalizeMessageContext = (messageContext) => {
    if (!messageContext?.key) return null;

    return {
        name: messageContext.name || '',
        type: messageContext.type || '',
        bucket: messageContext.bucket || '',
        key: messageContext.key,
    };
};

export const getAiChatPayload = (message = {}) => {
    if (message?.payload && typeof message.payload === 'object') {
        return message.payload;
    }

    return message;
};

export const getLatestMessageContext = (messages = []) => {
    const latestMessageWithContext = [...sortChatMessagesByCreatedOn(messages)]
        .reverse()
        .find((message) => message.message_context);

    return normalizeMessageContext(latestMessageWithContext?.message_context);
};

export const getDateKey = (value) => {
    const date = new Date(value);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export const preserveMarkdownLineBreaks = (text = '') => {
    return text.replace(/([^\n])\n([^\n])/g, '$1  \n$2');
};

export const hasAssistantStreamingOutput = ({ isRunning, messages }) => {
    if (!isRunning) return false;

    const latestMessage = messages[messages.length - 1];
    if (!latestMessage || latestMessage.role !== ChatRole.ASSISTANT) return false;

    return Boolean(getMessageText(latestMessage.content || []).trim());
};
